import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { Loader2 } from "lucide-react"
import { buttonVariants } from "@owlcat/ui"

import { cn } from "@/lib/utils"

/*
  DOCUMENTED FORK-BY-COMPOSITION of the @owlcat/ui Button.

  The shop composes the shared @owlcat/ui `buttonVariants` cva (so geometry and
  hover/focus styling converge on the fleet baseline; colours come from the shared
  bg-primary/bg-secondary/… tokens, which already resolve to the shop's warm
  palette) while preserving the shop's historical call-site API with ZERO churn:

    - `isLoading` prop (spinner) — not in @owlcat/ui Button.
    - forwardRef + `asChild` — @owlcat/ui Button is a plain function (no ref).
    - the shop's legacy variant/size NAMES, mapped onto the @owlcat/ui set:
        variant: primary→default, danger→destructive, transparent→ghost
        size:    base→default, small→sm, large→lg, xl/xlarge→lg
      (default/destructive/outline/secondary/ghost/link/success/warning/info and
       sm/lg/icon/icon-sm/icon-lg pass through unchanged.)
*/

type ShopVariant =
  | "default"
  | "primary"
  | "destructive"
  | "danger"
  | "outline"
  | "secondary"
  | "ghost"
  | "transparent"
  | "link"
  | "success"
  | "warning"
  | "info"

type ShopSize =
  | "default"
  | "base"
  | "sm"
  | "small"
  | "lg"
  | "large"
  | "xl"
  | "xlarge"
  | "icon"
  | "icon-sm"
  | "icon-lg"

type UIVariant = NonNullable<Parameters<typeof buttonVariants>[0]>["variant"]
type UISize = NonNullable<Parameters<typeof buttonVariants>[0]>["size"]

const variantMap: Record<ShopVariant, UIVariant> = {
  default: "default",
  primary: "default",
  destructive: "destructive",
  danger: "destructive",
  outline: "outline",
  secondary: "secondary",
  ghost: "ghost",
  transparent: "ghost",
  link: "link",
  success: "success",
  warning: "warning",
  info: "info",
}

const sizeMap: Record<ShopSize, UISize> = {
  default: "default",
  base: "default",
  sm: "sm",
  small: "sm",
  lg: "lg",
  large: "lg",
  xl: "lg",
  xlarge: "lg",
  icon: "icon",
  "icon-sm": "icon-sm",
  "icon-lg": "icon-lg",
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ShopVariant
  size?: ShopSize
  asChild?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size = "default",
      asChild = false,
      isLoading,
      children,
      disabled,
      type = "button",
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"
    // Radix Slot expects exactly one child — omit the spinner sibling in asChild.
    const content = asChild ? (
      children
    ) : (
      <>
        {isLoading && <Loader2 className="animate-spin" />}
        {children}
      </>
    )
    return (
      <Comp
        className={cn(
          buttonVariants({ variant: variantMap[variant], size: sizeMap[size] }),
          className
        )}
        ref={ref}
        disabled={disabled || isLoading}
        {...(asChild ? {} : { type })}
        {...props}
      >
        {content}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
